import styled from '@emotion/styled';
import { CircularProgress } from '@material-ui/core';
import { createElement, FC, Fragment, useEffect, useState } from 'react';

import ProductPricing from '../../../entity/ProductPricing';
import {
  OrganizationDetailFragment,
  ProjectDetailsForShowFragment,
  SingleDirectorUserFragment,
  useGetCreationSetWithPricingLazyQuery,
} from '../../../generated/graphql';
import {
  calculateTotalPointForAdditionalPurchase,
  CreationSetForCalculateTotalPointForAdditionalPurchase,
} from '../../../utility/calculatePoint';
import { SPACING_UNIT } from '../../constant/spacing';
import { CompetitionState } from '../../page/ShowProjectPage/ProposalList/ProjectProposalList';
import { ProposalAdoptButton, ProposalDialog } from './CreationsList';
import { AdditionalPurchasePriceDialog } from './CreationsList/AdditionalPurchasePriceDialog';

type Props = {
  adoptableCount: number;
  competitionState: CompetitionState;
  organization: OrganizationDetailFragment;
  creationSet: ProjectDetailsForShowFragment['creationSet'] | null | undefined;
  email: string;
  checkedProposals: string[];
  onAdoptionClicked: (checkedProposals: string[]) => Promise<void>;
  onPurchasedWithoutPayment: (checkedProposals: string[]) => Promise<void>;
  productPricing: ProductPricing | null;
  prepareUser: () => Promise<SingleDirectorUserFragment | void>;
};

export const AdditionalPurchase: FC<Props> = ({
  adoptableCount,
  competitionState,
  organization,
  creationSet,
  email,
  checkedProposals,
  onAdoptionClicked,
  onPurchasedWithoutPayment,
  productPricing,
  prepareUser,
}) => {
  const [openAdoptionDialog, setOpenAdoptionDialog] = useState(false);
  const [openPurchaseDialog, setOpenPurchaseDialog] = useState(false);
  const [stateEmail, setStateEmail] = useState(email);
  const checkedCount = checkedProposals.length;
  const [
    creationSetWithPrice,
    setCreationSetWithPrice,
  ] = useState<CreationSetForCalculateTotalPointForAdditionalPurchase>();
  const [fetchingPrice, setFetchingPrice] = useState(true);
  const [tooOldPricingProject, setTooOldPricingProject] = useState(false);

  const [loadCreationSet] = useGetCreationSetWithPricingLazyQuery();

  // 価格情報を持つcreationSetの取得
  useEffect(() => {
    const fetchPrice = async () => {
      if (creationSet) {
        try {
          const result = await loadCreationSet({ variables: { id: creationSet.id } });
          setCreationSetWithPrice(result.data?.creationSet || undefined);
          setFetchingPrice(false);
        } catch (err) {
          // コンペで古い価格データがあった場合用
          setTooOldPricingProject(true);
          setFetchingPrice(false);
        }
      }
    };
    fetchPrice();
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creationSet]);

  // 現在の保有ポイント
  const pointBalance = organization && organization.pointBalance != undefined ? organization.pointBalance : null;

  // 購入に必要なポイントの合計値
  const calcTotalPoint = (): number => {
    if (competitionState == 'purchasable' && !!creationSetWithPrice) {
      return calculateTotalPointForAdditionalPurchase(creationSetWithPrice, checkedCount);
    } else {
      return 0;
    }
  };

  // ポイントの不足の有無確認
  const pointShortageCheck = () => {
    const totalPoints = calcTotalPoint();
    const shortage = pointBalance == null || !totalPoints ? 0 : pointBalance - totalPoints;
    return shortage < 0;
  };

  // ボタンの挙動を保有・必要ポイントに応じて決める
  const createOnSubmittedPurchaseButton = (paymentUsed: boolean) => {
    if (paymentUsed) {
      return async () => {
        await setOpenPurchaseDialog(false);
        alert('ポイントが足りません'); // TODO エラーにする
      };
    } else {
      return async () => {
        await onPurchasedWithoutPayment(checkedProposals);
      };
    }
  };

  // 採用(追加購入)ボタン押した際の挙動
  // competitionStateに応じて開くダイアログを変える
  const onClickAdoptionButton = (competitionState: CompetitionState) => {
    if (competitionState == 'purchasable') {
      return () => setOpenPurchaseDialog(true);
    } else {
      return () => setOpenAdoptionDialog(true);
    }
  };

  useEffect(() => {
    // userのemailが空の場合
    const callPrepareUser = async () => {
      if (!stateEmail || stateEmail === '') {
        const user = await prepareUser();
        if (user && user.email) {
          setStateEmail(user.email);
        }
      }
    };
    callPrepareUser();
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // option epic1以前のpricingでは価格取得できないためボタン自体を表示しない
  if (tooOldPricingProject) return null;

  // 価格を取得していない場合はラベルとボタンを表示しない
  if (fetchingPrice) {
    return (
      <FetchingSpinner>
        <CircularProgress size={SPACING_UNIT * 4} color="primary" />
      </FetchingSpinner>
    );
  }

  return (
    productPricing && (
      <>
        {/* 採用・追加購入ボタン */}
        <ProposalAdoptButton
          adoptableCount={adoptableCount}
          checkedCount={checkedCount}
          competitionState={competitionState}
          onClickButton={onClickAdoptionButton(competitionState)}
        />
        {/* 採用確認ダイアログ */}
        <ProposalDialog
          open={openAdoptionDialog}
          onRequestClose={() => setOpenAdoptionDialog(false)}
          onSubmit={async () => onAdoptionClicked(checkedProposals)}
        />
        {/* 追加購入確認ダイアログ */}
        <AdditionalPurchasePriceDialog
          currentPointBalance={pointBalance}
          pointTotal={calcTotalPoint}
          purchaseAmount={checkedCount}
          open={openPurchaseDialog}
          onRequestClose={() => setOpenPurchaseDialog(false)}
          onSubmit={createOnSubmittedPurchaseButton(pointShortageCheck())}
          productPricing={productPricing}
        />
      </>
    )
  );
};

const FetchingSpinner = styled('div')({
  textAlign: 'end',
  marginRight: SPACING_UNIT * 5,
});
